// Shared Monte Carlo simulation engine
// Used by RetirementOutlook and InvestmentProjection widgets

function gaussian() {
  let u1, u2;
  do { u1 = Math.random(); } while (u1 === 0);
  do { u2 = Math.random(); } while (u2 === 0);
  return Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
}

/**
 * Run a Monte Carlo portfolio simulation.
 *
 * @param {object} opts
 * @param {number}  opts.currentBalance      Starting portfolio value
 * @param {number}  [opts.monthlyContrib=0]  Monthly contribution (positive) or withdrawal (negative)
 * @param {number}  opts.annualReturn         Expected annual return, percent (e.g. 7)
 * @param {number}  opts.annualVolatility     Annual volatility, percent (e.g. 15)
 * @param {number}  opts.years               Simulation horizon in years
 * @param {number}  [opts.simulations=500]   Number of paths
 * @param {number}  [opts.monthlyWithdrawal=0] Monthly withdrawal amount (drawdown phase)
 * @param {number}  [opts.targetAmount=null] If set, success = final balance >= targetAmount
 *
 * @returns {{ successRate, median, percentile10, percentile25, percentile75, percentile90, yearByYear }}
 *   yearByYear: array of { p10, p25, median, p75, p90 } for year 0..years
 */
export function runMonteCarlo({
  currentBalance,
  monthlyContrib = 0,
  annualReturn,
  annualVolatility,
  years,
  simulations = 500,
  monthlyWithdrawal = 0,
  targetAmount = null,
}) {
  const μ = (annualReturn / 100) / 12;
  const σ = (annualVolatility / 100) / Math.sqrt(12);
  const months = years * 12;

  // Pre-allocate per-year bucket arrays
  const yearBuckets = Array.from({ length: years + 1 }, () => []);
  const finals = [];

  for (let s = 0; s < simulations; s++) {
    let balance = currentBalance;
    let failed = false;
    yearBuckets[0].push(balance);

    for (let m = 1; m <= months; m++) {
      const r = μ + σ * gaussian();
      balance = balance * (1 + r) + monthlyContrib - monthlyWithdrawal;
      if (balance < 0) {
        balance = 0;
        failed = true;
      }
      if (m % 12 === 0) {
        yearBuckets[m / 12].push(balance);
      }
    }
    finals.push({ value: balance, failed });
  }

  finals.sort((a, b) => a.value - b.value);
  const vals = finals.map(f => f.value);

  const pctIdx = (p) => {
    const idx = Math.min(Math.floor((p / 100) * simulations), simulations - 1);
    return vals[idx];
  };

  const failCount = finals.filter(f =>
    f.failed || (targetAmount !== null && f.value < targetAmount)
  ).length;
  const successRate = Math.round(((simulations - failCount) / simulations) * 100);

  const yearByYear = yearBuckets.map(ys => {
    const sorted = [...ys].sort((a, b) => a - b);
    const n = sorted.length;
    const p = (pct) => sorted[Math.min(Math.floor((pct / 100) * n), n - 1)];
    return { p10: p(10), p25: p(25), median: p(50), p75: p(75), p90: p(90) };
  });

  return {
    successRate,
    median:        pctIdx(50),
    percentile10:  pctIdx(10),
    percentile25:  pctIdx(25),
    percentile75:  pctIdx(75),
    percentile90:  pctIdx(90),
    yearByYear,
  };
}
